import { GenericCurrency } from '../types/general'

/**
 * Compose a BRAND label for a vault: `${curator-or-brand} ${asset.symbol}`.
 *
 * Used by every vault parser to populate the cross-provider `displayName`
 * field. Keeps the synthesis rules in one place so output stays uniform —
 * `Steakhouse USDC`, `Gauntlet WETH`, `cp0x USDC`, `Fluid USDC`, `Silo USDC` —
 * regardless of which provider the entry came from.
 *
 * **NEVER use this as the vault's name.** It is uniform by construction and
 * therefore lossy by construction: every vault a curator runs on one asset
 * gets the same string. Four Gauntlet-curated USDC vaults holding $27M / $11M
 * / $10M / $5.8M collapsed into "Gauntlet USDC" when a consumer promoted it
 * over `name`, and because the label is built from the CURATOR it also
 * misattributes — the SwissBorg Morpho USDC vault is Gauntlet-curated, so it
 * was served as "Gauntlet USDC", someone else's product under Gauntlet's name.
 *
 * The vault's own `name` is the identity; this is the chip beside it, and the
 * curator has its own field for anyone grouping by curator.
 *
 * Fallback ladder:
 *   1. `${curatorName ?? brand} ${asset.symbol}` when the asset symbol
 *      is known (preferred path — both halves human-readable).
 *   2. `fallbackName` when the asset symbol is missing — preserves the
 *      raw on-chain share-token name rather than emitting `Morpho ` with
 *      a trailing space.
 *   3. The brand alone when even `fallbackName` is empty.
 */
export const composeVaultDisplayName = (
  brand: string,
  curatorName: string | undefined,
  asset: GenericCurrency | undefined,
  fallbackName: string | undefined,
): string => {
  const symbol = (asset?.symbol ?? '').trim()
  const label = (curatorName ?? brand).trim() || brand
  if (symbol) return `${label} ${symbol}`
  const fallback = (fallbackName ?? '').trim()
  return fallback || brand
}
